/**
 * Renderer de docs a HTML.
 *
 * Es la unica pieza que produce HTML en todo el proyecto: la usan tanto la
 * pagina publica (Astro) como el preview del admin (React). Un solo renderer
 * garantiza que lo que se ve antes de publicar sea lo mismo que se vera
 * despues.
 *
 * ## Modelo de seguridad
 * No hay sanitizado posterior a la generacion. El HTML se construye por
 * concatenacion de Tags conocidos y todo el contenido del autor pasa
 * SIEMPRE por `escapeHtml`. Los atributos se filtran con `safeUrl`. Esto
 * elimina por construccion el XSS por HTML pegado, enlaces o atributos,
 * en lugar de intentar bloquearlo despues con listas negras.
 */

import { escapeHtml, isInternalUrl, safeUrl, slugify } from "./text";
import type { FaqEntry, HeadingRef, RichDoc, RichMark, RichNode, TocEntry } from "./types";

export interface RenderOptions {
  /**
   * Prefijo de los anchors. Permite que el preview del admin genere ids
   * distintos a los reales y no interfiera con el indice ya insertado.
   */
  anchorPrefix?: string;
  /** Contenedor raiz. `null` devuelve solo los bloques. */
  wrapper?: "div" | "article" | null;
  /** Clase del contenedor raiz. */
  wrapperClass?: string;
  /** Añade `loading="lazy"` y `decoding="async"` a las imagenes de contenido. */
  lazyImages?: boolean;
}

export interface RenderResult {
  html: string;
  headings: HeadingRef[];
  toc: TocEntry[];
  faq: FaqEntry[];
}

const MARK_WRAPPERS: Record<string, (inner: string, mark: RichMark) => string> = {
  bold: (inner) => `<strong>${inner}</strong>`,
  italic: (inner) => `<em>${inner}</em>`,
  underline: (inner) => `<u>${inner}</u>`,
  strike: (inner) => `<s>${inner}</s>`,
  code: (inner) => `<code class="rc-inline-code">${inner}</code>`,
};

function renderMarked(node: RichNode): string {
  const base = escapeHtml(node.text ?? "");
  const marks = node.marks ?? [];
  return marks.reduce((inner, mark) => {
    if (mark.type === "link") {
      const href = safeUrl(String(mark.attrs?.href ?? ""));
      if (!href) return inner;
      const isInternal = isInternalUrl(href);
      const title = mark.attrs?.title ? ` title="${escapeHtml(String(mark.attrs.title))}"` : "";
      const rel = isInternal ? "" : ' rel="noopener noreferrer"';
      return `<a href="${escapeHtml(href)}"${title}${rel}>${inner}</a>`;
    }
    const wrapper = MARK_WRAPPERS[mark.type];
    return wrapper ? wrapper(inner, mark) : inner;
  }, base);
}

function inline(nodes: RichNode[] | undefined): string {
  if (!nodes || nodes.length === 0) return "";
  return nodes
    .map((node) => {
      if (node.type === "text") return renderMarked(node);
      if (node.type === "hardBreak") return "<br />";
      if (node.type === "image") return renderImage(node, { lazy: true, figure: false });
      return "";
    })
    .join("");
}

/** Convierte texto de un heading a un anchor estable y unico. */
function assignAnchor(text: string, seen: Map<string, number>, prefix: string): string {
  const base = slugify(text) || "seccion";
  const count = seen.get(base) ?? 0;
  seen.set(base, count + 1);
  const suffix = count === 0 ? "" : `-${count + 1}`;
  return `${prefix}${base}${suffix}`;
}

function collectHeadingsAndFaq(
  nodes: RichNode[],
  seen: Map<string, number>,
  prefix: string,
  headings: HeadingRef[],
  faq: FaqEntry[],
): void {
  for (const node of nodes) {
    if (node.type === "heading") {
      const level = node.attrs?.level === 3 ? 3 : 2;
      const label = node.content?.map((child) => child.text ?? "").join("") ?? "";
      const trimmed = label.trim();
      if (trimmed) {
        headings.push({ level, text: trimmed, anchor: assignAnchor(trimmed, seen, prefix) });
      }
      continue;
    }
    if (node.type === "faqItem") {
      const question = String(node.attrs?.question ?? "").trim();
      const answer = String(node.attrs?.answer ?? "").trim();
      if (question && answer) faq.push({ question, answer });
      continue;
    }
    if (node.content && node.content.length > 0) {
      collectHeadingsAndFaq(node.content, seen, prefix, headings, faq);
    }
  }
}

function renderImage(node: RichNode, options: { lazy: boolean; figure: boolean }): string {
  const src = safeUrl(String(node.attrs?.src ?? ""));
  if (!src) return "";

  const alt = node.attrs?.alt ? String(node.attrs.alt) : "";
  const title = node.attrs?.title ? String(node.attrs.title) : "";
  const caption = node.attrs?.caption ? String(node.attrs.caption) : "";

  const loading = options.lazy ? ' loading="lazy" decoding="async"' : ' loading="eager" decoding="async"';
  const titleAttr = title ? ` title="${escapeHtml(title)}"` : "";
  const img = `<img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}"${titleAttr}${loading} />`;

  // `<figure>` es contenido de bloque: emitirlo dentro de un `<p>` produce
  // HTML invalido y el navegador cierra el parrafo antes de tiempo. Solo los
  // imagenes de bloque llevan figure; las inline se quedan en `<img>`.
  if (!options.figure) return img;
  if (!caption) return `<figure class="rc-figure">${img}</figure>`;
  return `<figure class="rc-figure">${img}<figcaption class="rc-figcaption">${escapeHtml(caption)}</figcaption></figure>`;
}

function renderTable(node: RichNode): string {
  const rows = node.content ?? [];
  if (rows.length === 0) return "";

  const body: string[] = [];
  let head = "";

  for (const row of rows) {
    const cells = row.content ?? [];
    if (cells.length === 0) continue;

    const isHeaderRow = cells.every((cell) => cell.type === "tableHeader");
    const rendered = cells
      .map((cell) => {
        const tag = cell.type === "tableHeader" ? "th" : "td";
        const scope = cell.type === "tableHeader" ? ' scope="col"' : "";
        return `<${tag}${scope}>${inline(cell.content) || "&nbsp;"}</${tag}>`;
      })
      .join("");

    if (isHeaderRow && !head) {
      head = `<thead><tr>${rendered}</tr></thead>`;
    } else {
      body.push(`<tr>${rendered}</tr>`);
    }
  }

  if (!head && body.length === 0) return "";
  return `<div class="rc-table-wrap"><table class="rc-table">${head}<tbody>${body.join("")}</tbody></table></div>`;
}

function renderFaqList(node: RichNode): string {
  const items = (node.content ?? [])
    .map((item) => {
      const question = String(item.attrs?.question ?? "").trim();
      const answer = String(item.attrs?.answer ?? "").trim();
      if (!question || !answer) return "";
      return `<details class="rc-faq-item"><summary class="rc-faq-question">${escapeHtml(question)}</summary><div class="rc-faq-answer"><p>${escapeHtml(answer)}</p></div></details>`;
    })
    .filter(Boolean);

  if (items.length === 0) return "";
  return `<div class="rc-faq">${items.join("")}</div>`;
}

function renderBlocks(
  nodes: RichNode[],
  anchors: Map<RichNode, string>,
  options: Required<Pick<RenderOptions, "lazyImages">>,
): string {
  return nodes
    .map((node) => {
      switch (node.type) {
        case "paragraph": {
          const inner = inline(node.content);
          return inner ? `<p>${inner}</p>` : "<p><br /></p>";
        }
        case "heading": {
          const level = node.attrs?.level === 3 ? 3 : 2;
          const anchor = anchors.get(node);
          const inner = inline(node.content);
          const idAttr = anchor ? ` id="${escapeHtml(anchor)}"` : "";
          // El H1 nunca sale del editor: se degrada a H2 si llega desde otro origen.
          return `<h${level}${idAttr} class="rc-h${level}">${inner}</h${level}>`;
        }
        case "bulletList":
        case "orderedList": {
          const tag = node.type === "orderedList" ? "ol" : "ul";
          const items = (node.content ?? [])
            .map((item) => `<li>${renderBlocks(item.content ?? [], anchors, options)}</li>`)
            .join("");
          return items ? `<${tag} class="rc-list">${items}</${tag}>` : "";
        }
        case "listItem":
          return renderBlocks(node.content ?? [], anchors, options);
        case "blockquote":
          return `<blockquote class="rc-quote"><p>${inline(node.content)}</p></blockquote>`;
        case "codeBlock":
          return `<pre class="rc-code"><code>${inline(node.content)}</code></pre>`;
        case "horizontalRule":
          return '<hr class="rc-rule" />';
        case "table":
          return renderTable(node);
        case "image":
          return renderImage(node, { lazy: options.lazyImages, figure: true });
        case "faqList":
          return renderFaqList(node);
        default:
          // Nodo desconocido: se ignora en lugar de emitir markup arbitrario.
          return node.content ? renderBlocks(node.content, anchors, options) : "";
      }
    })
    .join("\n");
}

export function renderRichContent(doc: RichDoc, options: RenderOptions = {}): RenderResult {
  const prefix = options.anchorPrefix ?? "";
  const lazyImages = options.lazyImages ?? true;

  const seen = new Map<string, number>();
  const headings: HeadingRef[] = [];
  const faq: FaqEntry[] = [];
  collectHeadingsAndFaq(doc.content, seen, prefix, headings, faq);

  const anchors = new Map<RichNode, string>();
  const seenAgain = new Map<string, number>();
  const assign = (nodes: RichNode[]) => {
    for (const node of nodes) {
      if (node.type === "heading") {
        const label = (node.content ?? [])
          .map((child) => child.text ?? "")
          .join("")
          .trim();
        if (label) anchors.set(node, assignAnchor(label, seenAgain, prefix));
        continue;
      }
      if (node.content) assign(node.content);
    }
  };
  assign(doc.content);

  const inner = renderBlocks(doc.content, anchors, { lazyImages });

  const wrapper = options.wrapper === undefined ? "div" : options.wrapper;
  const html =
    wrapper === null
      ? inner
      : `<${wrapper} class="${escapeHtml(options.wrapperClass ?? "rc-prose")}">${inner}</${wrapper}>`;

  return {
    html,
    headings,
    // Solo H2 y H3 entran al indice; el H1 es el titulo de la publicacion.
    toc: headings.map((heading) => ({ level: heading.level, text: heading.text, anchor: heading.anchor })),
    faq,
  };
}

/** Indice automatico a partir del doc. Reusa los anchors del render. */
export function buildToc(doc: RichDoc, anchorPrefix = ""): TocEntry[] {
  const seen = new Map<string, number>();
  const headings: HeadingRef[] = [];
  const faq: FaqEntry[] = [];
  collectHeadingsAndFaq(doc.content, seen, anchorPrefix, headings, faq);
  return headings.map((heading) => ({ level: heading.level, text: heading.text, anchor: heading.anchor }));
}
